import { P, PermissionCode, Scope } from './permissions';

/**
 * Starter role templates copied into every new tenant. Tenants can edit these (except that
 * `TENANT_OWNER` always keeps role-management rights so a tenant cannot lock itself out) or
 * create their own. Nothing here is mTouch-specific.
 *
 * Platform Administrator is intentionally NOT a tenant role: it is a flag on the global user
 * identity and only unlocks tenant lifecycle endpoints — never routine HR content.
 */
export type RoleGrant = [PermissionCode, Scope];

export interface RoleTemplate {
  key: string;
  name: string;
  description: string;
  grants: RoleGrant[];
}

const tenant = (...codes: PermissionCode[]): RoleGrant[] => codes.map((c) => [c, 'TENANT']);
const self = (...codes: PermissionCode[]): RoleGrant[] => codes.map((c) => [c, 'SELF']);
const team = (...codes: PermissionCode[]): RoleGrant[] => codes.map((c) => [c, 'TEAM']);

const EMPLOYEE_SELF_SERVICE: RoleGrant[] = self(
  P.EMPLOYEE_READ,
  P.EMPLOYEE_SELF_UPDATE,
  P.EMPLOYEE_PERSONAL_VIEW,
  P.EMPLOYEE_BANK_VIEW,
  P.EMPLOYEE_GOVID_VIEW,
  P.EMPLOYEE_SALARY_VIEW,
  P.EMPLOYEE_IDDOC_VIEW,
  P.EMPLOYEE_CUSTOM_SENSITIVE_VIEW,
  P.DOCUMENT_READ,
  P.DOCUMENT_UPLOAD,
  P.ONBOARDING_CASE_READ,
  P.LEAVE_BALANCE_READ,
  P.LEAVE_REQUEST_READ,
  P.LEAVE_CREATE,
  P.LEAVE_CANCEL,
  P.ASSET_READ,
  P.ASSET_ACKNOWLEDGE,
);

const ORG_READ: RoleGrant[] = tenant(P.DEPARTMENT_READ, P.TEAM_READ, P.LOCATION_READ, P.CALENDAR_READ, P.LEAVE_POLICY_READ);

export const ROLE_TEMPLATES: RoleTemplate[] = [
  {
    key: 'TENANT_OWNER',
    name: 'Company Admin',
    description: 'Tenant owner: configuration, users and role management within their authority.',
    grants: [
      ...tenant(
        P.TENANT_READ,
        P.TENANT_UPDATE,
        P.CONFIG_READ,
        P.CONFIG_UPDATE,
        P.USER_READ,
        P.USER_INVITE,
        P.USER_DEACTIVATE,
        P.ROLE_READ,
        P.ROLE_CREATE,
        P.ROLE_UPDATE,
        P.ROLE_ASSIGN,
        P.DEPARTMENT_MANAGE,
        P.TEAM_MANAGE,
        P.LOCATION_CREATE,
        P.LOCATION_UPDATE,
        P.LOCATION_CODE_UPDATE,
        P.LOCATION_DEACTIVATE,
        P.LOCATION_DEACTIVATE_OVERRIDE,
        P.CALENDAR_MANAGE,
        P.CUSTOM_FIELD_MANAGE,
        P.DOCUMENT_CATEGORY_MANAGE,
        P.EMPLOYEE_READ,
        P.EMPLOYEE_CREATE,
        P.EMPLOYEE_UPDATE,
        P.EMPLOYEE_TRANSFER,
        P.EMPLOYEE_STATUS_UPDATE,
        P.EMPLOYEE_ARCHIVE,
        P.EMPLOYEE_EXPORT,
        P.EMPLOYEE_IMPORT,
        P.EMPLOYEE_IMPORT_UPDATE,
        P.EMPLOYEE_NUMBER_UPDATE,
        P.EMPLOYEE_CHANGE_REVIEW,
        P.EMPLOYEE_PERSONAL_VIEW,
        P.DOCUMENT_READ,
        P.DOCUMENT_UPLOAD,
        P.DOCUMENT_REVIEW,
        P.DOCUMENT_DELETE,
        P.ONBOARDING_TEMPLATE_READ,
        P.ONBOARDING_TEMPLATE_MANAGE,
        P.ONBOARDING_CASE_READ,
        P.ONBOARDING_CASE_CREATE,
        P.ONBOARDING_CASE_MANAGE,
        P.ONBOARDING_TASK_REVIEW,
        P.LEAVE_TYPE_MANAGE,
        P.LEAVE_POLICY_MANAGE,
        P.LEAVE_BALANCE_READ,
        P.LEAVE_BALANCE_ADJUST,
        P.LEAVE_REQUEST_READ,
        P.LEAVE_CREATE,
        P.LEAVE_APPROVE,
        P.LEAVE_REJECT,
        P.LEAVE_CANCEL,
        P.LEAVE_OVERRIDE,
        P.ASSET_READ,
        P.ASSET_CREATE,
        P.ASSET_UPDATE,
        P.ASSET_ASSIGN,
        P.ASSET_ASSIGN_OVERRIDE,
        P.ASSET_RETURN,
        P.ASSET_TAG,
        P.ASSET_UNTAG,
        P.ASSET_CONFIG_MANAGE,
        P.ASSET_AUDIT_VIEW,
        P.ASSET_AUDIT_EXPORT,
        P.ASSET_FINANCIAL_VIEW,
        P.REPORT_VIEW,
        P.REPORT_EXPORT,
        P.AUDIT_VIEW,
        P.AUDIT_EXPORT,
      ),
      ...ORG_READ,
      ...EMPLOYEE_SELF_SERVICE,
    ],
  },
  {
    key: 'HR_ADMIN',
    name: 'HR Administrator',
    description: 'Employee lifecycle, onboarding, leave, documents and HR reports.',
    grants: [
      ...ORG_READ,
      ...tenant(
        P.TENANT_READ,
        P.CONFIG_READ,
        P.USER_READ,
        P.USER_INVITE,
        P.ROLE_READ,
        P.DEPARTMENT_MANAGE,
        P.TEAM_MANAGE,
        P.CUSTOM_FIELD_MANAGE,
        P.DOCUMENT_CATEGORY_MANAGE,
        P.EMPLOYEE_READ,
        P.EMPLOYEE_CREATE,
        P.EMPLOYEE_UPDATE,
        P.EMPLOYEE_TRANSFER,
        P.EMPLOYEE_STATUS_UPDATE,
        P.EMPLOYEE_ARCHIVE,
        P.EMPLOYEE_EXPORT,
        P.EMPLOYEE_IMPORT,
        P.EMPLOYEE_IMPORT_UPDATE,
        P.EMPLOYEE_CHANGE_REVIEW,
        P.EMPLOYEE_PERSONAL_VIEW,
        P.EMPLOYEE_GOVID_VIEW,
        P.EMPLOYEE_GOVID_EDIT,
        P.EMPLOYEE_IDDOC_VIEW,
        P.EMPLOYEE_CUSTOM_SENSITIVE_VIEW,
        P.DOCUMENT_READ,
        P.DOCUMENT_UPLOAD,
        P.DOCUMENT_REVIEW,
        P.DOCUMENT_DELETE,
        P.ONBOARDING_TEMPLATE_READ,
        P.ONBOARDING_TEMPLATE_MANAGE,
        P.ONBOARDING_CASE_READ,
        P.ONBOARDING_CASE_CREATE,
        P.ONBOARDING_CASE_MANAGE,
        P.ONBOARDING_TASK_REVIEW,
        P.LEAVE_TYPE_MANAGE,
        P.LEAVE_POLICY_MANAGE,
        P.LEAVE_BALANCE_READ,
        P.LEAVE_BALANCE_ADJUST,
        P.LEAVE_REQUEST_READ,
        P.LEAVE_CREATE,
        P.LEAVE_APPROVE,
        P.LEAVE_REJECT,
        P.LEAVE_CANCEL,
        P.LEAVE_OVERRIDE,
        P.ASSET_READ,
        P.REPORT_VIEW,
        P.REPORT_EXPORT,
      ),
      ...EMPLOYEE_SELF_SERVICE,
    ],
  },
  {
    key: 'PAYROLL_FINANCE',
    name: 'Payroll / Finance',
    description: 'Compensation and bank information only — no general employee administration.',
    grants: [
      ...tenant(P.EMPLOYEE_SALARY_VIEW, P.EMPLOYEE_SALARY_EDIT, P.EMPLOYEE_BANK_VIEW, P.EMPLOYEE_BANK_EDIT),
      ...EMPLOYEE_SELF_SERVICE,
    ],
  },
  {
    key: 'MANAGER',
    name: 'Manager',
    description: 'Assigned team: approvals and limited employee information.',
    grants: [
      ...ORG_READ,
      ...team(
        P.EMPLOYEE_READ,
        P.LEAVE_REQUEST_READ,
        P.LEAVE_BALANCE_READ,
        P.LEAVE_APPROVE,
        P.LEAVE_REJECT,
        P.ONBOARDING_CASE_READ,
        P.ASSET_READ,
        P.DOCUMENT_READ,
      ),
      ...EMPLOYEE_SELF_SERVICE,
    ],
  },
  {
    key: 'EMPLOYEE',
    name: 'Employee',
    description: 'Own profile, leave, onboarding, documents and assets.',
    grants: [...tenant(P.DEPARTMENT_READ, P.TEAM_READ, P.LOCATION_READ, P.CALENDAR_READ, P.LEAVE_POLICY_READ), ...EMPLOYEE_SELF_SERVICE],
  },
  {
    key: 'ASSET_CUSTODIAN',
    name: 'IT / Asset Custodian',
    description: 'Asset inventory, assignment, return, tags and custody audit.',
    grants: [
      ...ORG_READ,
      ...tenant(
        P.ASSET_READ,
        P.ASSET_CREATE,
        P.ASSET_UPDATE,
        P.ASSET_ASSIGN,
        P.ASSET_ASSIGN_OVERRIDE,
        P.ASSET_RETURN,
        P.ASSET_TAG,
        P.ASSET_UNTAG,
        P.ASSET_CONFIG_MANAGE,
        P.ASSET_AUDIT_VIEW,
        P.ASSET_AUDIT_EXPORT,
        P.REPORT_VIEW,
      ),
      ...EMPLOYEE_SELF_SERVICE,
    ],
  },
  {
    key: 'AUDITOR',
    name: 'Auditor (Read Only)',
    description: 'Read-only access to assigned scope and the audit log. Exports only if explicitly granted.',
    grants: [
      ...ORG_READ,
      ...tenant(
        P.TENANT_READ,
        P.CONFIG_READ,
        P.USER_READ,
        P.ROLE_READ,
        P.EMPLOYEE_READ,
        P.ONBOARDING_TEMPLATE_READ,
        P.ONBOARDING_CASE_READ,
        P.LEAVE_REQUEST_READ,
        P.LEAVE_BALANCE_READ,
        P.ASSET_READ,
        P.ASSET_AUDIT_VIEW,
        P.REPORT_VIEW,
        P.AUDIT_VIEW,
      ),
      ...EMPLOYEE_SELF_SERVICE,
    ],
  },
];

export const TENANT_OWNER_ROLE_KEY = 'TENANT_OWNER';
export const DEFAULT_EMPLOYEE_ROLE_KEY = 'EMPLOYEE';
